import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Card, CardContent, Typography, Button, TextField, Tab, Tabs, Alert, Snackbar, Select, MenuItem, FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Instagram, Search, People, Image as ImageIcon, Comment } from '@mui/icons-material';
import { getAccounts, selectAccount } from '../redux/slices/instagramSlice';
import axios from 'axios';

const InstagramManager = () => {
    const dispatch = useDispatch();
    const { accounts, selectedAccount, loading: accountsLoading } = useSelector((state) => state.instagram);
    const token = useSelector((state) => state.auth.token);
    const [currentTab, setCurrentTab] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Scraping data
    const [followers, setFollowers] = useState([]);
    const [following, setFollowing] = useState([]);
    const [posts, setPosts] = useState([]);
    const [comments, setComments] = useState([]);
    const [mediaId, setMediaId] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        dispatch(getAccounts());
    }, [dispatch]);

    const handleScrapeFollowers = async () => {
        if (!selectedAccount) {
            setSnackbar({ open: true, message: 'يرجى اختيار حساب أولاً', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(`https://social-auto-r8we.onrender.com/api/instagram/followers/${selectedAccount}`, config);
            setFollowers(data.data || []);
            setSnackbar({ open: true, message: '✅ تم جمع المتابعين بنجاح!', severity: 'success' });
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'فشل جمع البيانات', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleScrapeFollowing = async () => {
        if (!selectedAccount) {
            setSnackbar({ open: true, message: 'يرجى اختيار حساب أولاً', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(`https://social-auto-r8we.onrender.com/api/instagram/following/${selectedAccount}`, config);
            setFollowing(data.data || []);
            setSnackbar({ open: true, message: '✅ تم جمع المتابَعين بنجاح!', severity: 'success' });
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'فشل جمع البيانات', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleScrapeComments = async () => {
        if (!selectedAccount) {
            setSnackbar({ open: true, message: 'يرجى اختيار حساب أولاً', severity: 'error' });
            return;
        }

        if (!mediaId) {
            setSnackbar({ open: true, message: 'يرجى إدخال معرف المنشور', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const config = {
                headers: { Authorization: `Bearer ${token}` },
                params: { accountId: selectedAccount }
            };
            const { data } = await axios.get(`https://social-auto-r8we.onrender.com/api/instagram/comments/${mediaId}`, config);
            setComments(data.data || []);
            setSnackbar({ open: true, message: '✅ تم جمع التعليقات بنجاح!', severity: 'success' });
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'فشل جمع البيانات', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleScrapePosts = async () => {
        if (!selectedAccount) {
            setSnackbar({ open: true, message: 'يرجى اختيار حساب أولاً', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(`https://social-auto-r8we.onrender.com/api/instagram/posts/${selectedAccount}`, config);
            setPosts(data.data || []);
            setSnackbar({ open: true, message: '✅ تم جمع المنشورات بنجاح!', severity: 'success' });
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || error.message || 'فشل جمع البيانات', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const selectedAccountData = accounts.find(a => a._id === selectedAccount);

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', borderRadius: 3, p: { xs: 3, md: 4 }, mb: 3, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Instagram sx={{ fontSize: { xs: 32, md: 40 } }} />
                        <Box>
                            <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                                Instagram Scraping
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>جمع البيانات والمعلومات</Typography>
                        </Box>
                    </Box>
                    {accounts.length > 0 && (
                        <FormControl sx={{ minWidth: 250, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                            <InputLabel sx={{ color: 'white' }}>الحساب النشط</InputLabel>
                            <Select value={selectedAccount || ''} onChange={(e) => dispatch(selectAccount(e.target.value))} sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}>
                                {accounts.map((account) => (<MenuItem key={account._id} value={account._id}>@{account.username}</MenuItem>))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                        <Tabs
                            value={currentTab}
                            onChange={(e, newValue) => setCurrentTab(newValue)}
                            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                        >
                            <Tab icon={<People />} label="المتابعين" disabled={!selectedAccount} />
                            <Tab icon={<People />} label="المتابَعين" disabled={!selectedAccount} />
                            <Tab icon={<ImageIcon />} label="المنشورات" disabled={!selectedAccount} />
                            <Tab icon={<Comment />} label="التعليقات" disabled={!selectedAccount} />
                        </Tabs>

                        <CardContent sx={{ p: 4 }}>
                            {!selectedAccount && (
                                <Alert severity="info">يرجى اختيار حساب من القائمة أعلاه للبدء</Alert>
                            )}

                            {/* Followers Tab */}
                            {currentTab === 0 && selectedAccount && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>جمع المتابعين (Followers)</Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<Search />}
                                        onClick={handleScrapeFollowers}
                                        disabled={loading}
                                        sx={{ mb: 3, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
                                    >
                                        {loading ? 'جاري الجمع...' : 'جمع المتابعين'}
                                    </Button>

                                    {followers.length > 0 && (
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>اسم المستخدم</TableCell>
                                                        <TableCell>الاسم الكامل</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {followers.map((follower, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{follower.username}</TableCell>
                                                            <TableCell>{follower.fullName}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}

                            {/* Following Tab */}
                            {currentTab === 1 && selectedAccount && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>جمع المتابَعين (Following)</Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<Search />}
                                        onClick={handleScrapeFollowing}
                                        disabled={loading}
                                        sx={{ mb: 3, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
                                    >
                                        {loading ? 'جاري الجمع...' : 'جمع المتابَعين'}
                                    </Button>

                                    {following.length > 0 && (
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>اسم المستخدم</TableCell>
                                                        <TableCell>الاسم الكامل</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {following.map((user, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{user.username}</TableCell>
                                                            <TableCell>{user.fullName}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}

                            {/* Posts Tab */}
                            {currentTab === 2 && selectedAccount && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>جمع المنشورات (Posts)</Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<Search />}
                                        onClick={handleScrapePosts}
                                        disabled={loading}
                                        sx={{ mb: 3, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
                                    >
                                        {loading ? 'جاري الجمع...' : 'جمع المنشورات'}
                                    </Button>

                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        سيتم جمع آخر المنشورات من الحساب المحدد
                                    </Alert>

                                    {posts.length > 0 && (
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>المعرف</TableCell>
                                                        <TableCell>الصورة</TableCell>
                                                        <TableCell>النص</TableCell>
                                                        <TableCell>الإعجابات</TableCell>
                                                        <TableCell>التعليقات</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {posts.map((post, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                                                                {post.id}
                                                            </TableCell>
                                                            <TableCell>
                                                                {post.imageUrl && (
                                                                    <img src={post.imageUrl} alt="post" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} />
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ maxWidth: 300 }}>
                                                                {post.caption ? (post.caption.substring(0, 100) + (post.caption.length > 100 ? '...' : '')) : 'بدون نص'}
                                                            </TableCell>
                                                            <TableCell>{post.likeCount || 0}</TableCell>
                                                            <TableCell>{post.commentCount || 0}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}

                            {/* Comments Tab */}
                            {currentTab === 3 && selectedAccount && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>جمع التعليقات (Comments)</Typography>
                                    <Alert severity="warning" sx={{ mb: 3 }}>
                                        <strong>📌 كيفية الحصول على Media ID:</strong><br />
                                        1. اذهب لتبويب "المنشورات" → اضغط "جمع المنشورات"<br />
                                        2. انسخ الرقم الطويل من عمود "المعرف"<br />
                                        3. الصقه أدناه لجمع التعليقات
                                    </Alert>

                                    <TextField
                                        fullWidth
                                        label="معرف المنشور (Media ID)"
                                        placeholder="مثال: 3234567890123456789"
                                        value={mediaId}
                                        onChange={(e) => setMediaId(e.target.value)}
                                        sx={{ mb: 3 }}
                                        helperText="⚠️ يجب أن يكون رقم طويل (من جدول المنشورات) وليس code قصير"
                                    />

                                    <Button
                                        variant="contained"
                                        startIcon={<Search />}
                                        onClick={handleScrapeComments}
                                        disabled={loading || !mediaId}
                                        sx={{ mb: 3, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
                                    >
                                        {loading ? 'جاري الجمع...' : 'جمع التعليقات'}
                                    </Button>

                                    {comments.length > 0 && (
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>المستخدم</TableCell>
                                                        <TableCell>التعليق</TableCell>
                                                        <TableCell>الإعجابات</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {comments.map((comment, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{comment.user?.username || 'Unknown'}</TableCell>
                                                            <TableCell>{comment.text}</TableCell>
                                                            <TableCell>{comment.likeCount || 0}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default InstagramManager;
